import { differenceInYears, format, isToday, parseISO } from "date-fns";

import { PRONOUNS } from "constants/patients";
import type { Patient } from "services/types/patients.type";
import { convertDateInTimezoneToCurrent } from "utils/common";

// Call-time context helpers shared by the softphone patient surfaces (the
// compact row and the incoming-call hero). Fed by the
// INCOMING_CALL_PATIENTS_QUERY field set.

export function appointmentContext(patient: Patient) {
  const appointment = patient.next_appointment;
  if (!appointment?.start_moment) return null;
  const localStart = convertDateInTimezoneToCurrent(
    appointment.start_moment,
    appointment.clinic?.timezone
  );
  const time = format(localStart, "h:mm a");
  const when = isToday(localStart)
    ? `Appointment today at ${time}`
    : `Next appt: ${format(localStart, "MMM d")} at ${time}`;
  const shortWhen = isToday(localStart)
    ? `Today ${time}`
    : `${format(localStart, "MMM d")}, ${time}`;
  const staffMember = appointment.staff_member;
  const provider = staffMember
    ? `${staffMember.first_name ?? ""} ${staffMember.last_name ?? ""}`.trim()
    : "";
  const details = [appointment.type?.name, provider]
    .filter(Boolean)
    .join(" · ");
  return { when, shortWhen, details: details || null };
}

export function balanceDue(patient: Patient) {
  return parseFloat(patient.patient_responsibility_balance || "0");
}

export function pendingFormsCount(patient: Patient) {
  return patient.number_of_pending_intake_forms ?? 0;
}

// "She/her/hers · 78 · Patient since 2022" — whichever parts are on file.
export function identityLine(patient: Patient) {
  const parts: string[] = [];
  const pronouns = PRONOUNS.getFromCode(patient.pronouns);
  if (pronouns) parts.push(pronouns);
  if (patient.birthdate) {
    parts.push(
      String(differenceInYears(new Date(), parseISO(patient.birthdate)))
    );
  }
  if (patient.created_at) {
    parts.push(`Patient since ${format(parseISO(patient.created_at), "yyyy")}`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

// An active primary policy names the insurer; otherwise fall back to the
// declared payment source ("Private Pay" / "Managed Care"). No policy and no
// declaration reads as private pay — that is what "nothing on file" means to
// the front desk.
export function paymentSourceLabel(patient: Patient) {
  if (patient.primary_insurance_policy) {
    return patient.primary_insurance_policy.insurer?.name || "Insured";
  }
  return patient.payment_source_type_display || "Private pay";
}

// Fitted-per-ear hearing aid fields: a delivered or in-use device makes the
// patient a wearer; a device on file that is neither is a pending order.
export function hearingAidLabel(patient: Patient) {
  const devices = [
    patient.left_ear_hearing_aid,
    patient.right_ear_hearing_aid
  ].filter(Boolean);
  if (devices.length === 0) return null;
  const worn = devices.some(
    (device) => device?.is_in_use || device?.delivered_at
  );
  return worn ? "Hearing aid user" : "Hearing aids ordered";
}

// The patient's tags that still apply. The backend hands back every assigned
// tag bar discarded and conversation ones, expired included, so expiry is
// checked here. Newest first, the same order the tag list keeps when it caps
// how many it shows, so the pop leads with what staff added most recently.
export function activeTags(patient: Patient, now = new Date()) {
  return (patient.assigned_tags ?? [])
    .filter((assigned) => assigned.tag)
    .filter(
      (assigned) => !assigned.expires_at || new Date(assigned.expires_at) >= now
    )
    .sort((a, b) => b.id - a.id);
}

type NamedStaff = {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string;
  fullName?: string;
};

// "Dr Amy Chen Au.D." — the backend's full_name carries title and suffix.
function staffName(staff: NamedStaff) {
  return (
    staff.full_name ||
    staff.fullName ||
    `${staff.first_name ?? ""} ${staff.last_name ?? ""}`.trim()
  );
}

// Who the patient sees: the preferred provider first, then the secondary one
// with their role after the name ("Jane Roe (Speech Therapy)"), whichever
// are on file.
export function providerNames(patient: Patient) {
  const names: string[] = [];
  if (patient.preferred_provider) {
    const name = staffName(patient.preferred_provider);
    if (name) names.push(name);
  }
  if (patient.secondary_preferred_provider) {
    const name = staffName(patient.secondary_preferred_provider);
    const role = patient.secondary_preferred_provider_role?.trim();
    if (name) names.push(role ? `${name} (${role})` : name);
  }
  return names;
}

// One line of provider names. The separator is glued to the name before it
// (non-breaking space), so a wrapped line never starts with a stray "·".
export function providerLine(names: string[]) {
  return names.join("\u00a0· ");
}
